import { Button } from '@monorepo/shared/componentsV2/Button';
import {
  WizardHeader,
  WizardLink,
} from '@monorepo/shared/componentsV2/fullPageWizard/WizardHeader';
import { getLinkToAdminFormTemplateEditor } from '@monorepo/shared/externalLinks/adminPanel';
import { getOrgSpecificLink } from '@monorepo/shared/hooks/navigation/useLinkOnSame/useLinkOnSameOrg';
import { useReferrerUrl } from '@monorepo/shared/hooks/navigation/useReferrerUrl';
import { useHasOrganizationUpdatePermissions } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { useIsSuperAdmin } from '@monorepo/shared/hooks/permissions/useIsSuperAdmin';
import { getPathToHome } from '@monorepo/shared/routing';
import { NonEmptyArray } from 'mapistry-shared';
import { useFeatureFlags } from 'mapistry-shared/api';
import React, { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useFormTemplateContext } from '../EditFormTemplatePage/FormTemplateContext';
import { WizardStep } from './consts';
import { getLinkToFormTemplateWizardStep } from './useNavigateToFormTemplateWizardStep';

const AdminButton = styled(Button)`
  margin-right: 0.5rem;
`;

/** if the page we came from set a url to return to, use that on close, otherwise return to form list overview */
export function useCloseUrl() {
  const { organizationId } = useParams();

  const homeLink = getPathToHome();
  const defaultCloseUrl = organizationId
    ? getOrgSpecificLink({ organizationId, pathEnd: '/data-setup/forms' })
    : homeLink;

  return useReferrerUrl({ defaultReferrerUrl: defaultCloseUrl });
}

function useGetBreadcrumbsLinks({
  formWizardBasePath,
  isBasicInfoInvalid,
  isEditMode,
  isSubmitting,
  wizardStep,
  organizationId,
}: {
  formWizardBasePath: string;
  isBasicInfoInvalid?: boolean;
  isEditMode: boolean;
  isSubmitting: boolean;
  wizardStep: WizardStep;
  organizationId?: string;
}) {
  const { formTemplateSlug = null } = useParams();
  const hasOrgUpdatePermissions =
    useHasOrganizationUpdatePermissions(organizationId);
  const { isAssigningFormCalendarsEnabled } = useFeatureFlags();

  const isUsingOrgSpecificEditor = !!organizationId;
  const canEditCalendars =
    isAssigningFormCalendarsEnabled &&
    isUsingOrgSpecificEditor &&
    hasOrgUpdatePermissions;

  const breadcrumbsLinks = useMemo<NonEmptyArray<WizardLink>>(() => {
    const breadcrumbs: NonEmptyArray<WizardLink> = [
      {
        completed: wizardStep === WizardStep.FIELDS,
        disabled: isSubmitting,
        label: WizardStep.DETAILS,
        to: getLinkToFormTemplateWizardStep({
          stepToGoTo: WizardStep.DETAILS,
          formTemplateSlug,
          formWizardBasePath,
        }),
      },
      {
        disabled: isBasicInfoInvalid || isSubmitting,
        label: WizardStep.FIELDS,
        to: getLinkToFormTemplateWizardStep({
          stepToGoTo: WizardStep.FIELDS,
          formTemplateSlug,
          formWizardBasePath,
        }),
      },
      ...(canEditCalendars
        ? [
            {
              disabled: !isEditMode || isBasicInfoInvalid || isSubmitting,
              label: WizardStep.CALENDARS,
              to: getLinkToFormTemplateWizardStep({
                stepToGoTo: WizardStep.CALENDARS,
                formTemplateSlug,
                formWizardBasePath,
              }),
            },
          ]
        : []),
    ];
    return breadcrumbs;
  }, [
    canEditCalendars,
    formTemplateSlug,
    formWizardBasePath,
    isBasicInfoInvalid,
    isEditMode,
    isSubmitting,
    wizardStep,
  ]);

  return breadcrumbsLinks;
}

type EditFormTemplateHeaderProps = {
  isNavigatingInWizardEnabled?: boolean;
  canSubmit: boolean;
  formWizardBasePath: string;
  isBasicInfoInvalid?: boolean;
  isEditMode: boolean;
  isSubmitting: boolean;
  onSubmit?: () => void;
  organizationId?: string;
  rightActions?: React.ComponentProps<typeof WizardHeader>['rightActions'];
  wizardStep: WizardStep;
};

export function FormTemplateHeader({
  isNavigatingInWizardEnabled = true,
  canSubmit,
  formWizardBasePath,
  isBasicInfoInvalid,
  isEditMode,
  isSubmitting,
  onSubmit,
  organizationId,
  rightActions: rightActionsProp,
  wizardStep,
}: EditFormTemplateHeaderProps) {
  const navigate = useNavigate();
  const closeUrl = useCloseUrl();
  const { savedName: formTemplateName } = useFormTemplateContext();
  const breadcrumbsLinks = useGetBreadcrumbsLinks({
    formWizardBasePath,
    isBasicInfoInvalid,
    isEditMode,
    isSubmitting,
    organizationId,
    wizardStep,
  });
  const isSuperAdmin = useIsSuperAdmin();
  const { formTemplateSlug = null } = useParams();

  const handleClose = useCallback(
    () => navigate(closeUrl),
    [closeUrl, navigate],
  );

  const rightActions = useMemo(
    () => (
      <>
        {formTemplateSlug && isSuperAdmin && (
          <AdminButton
            href={getLinkToAdminFormTemplateEditor({ formTemplateSlug })}
          >
            Admin Editor
          </AdminButton>
        )}
        {rightActionsProp}
      </>
    ),
    [formTemplateSlug, isSuperAdmin, rightActionsProp],
  );

  return (
    <WizardHeader
      isNavigatingInWizardEnabled={isNavigatingInWizardEnabled}
      canSubmit={!isSubmitting && canSubmit}
      isEditMode={isEditMode}
      isSubmitting={isSubmitting}
      links={breadcrumbsLinks}
      onClose={handleClose}
      onSubmit={onSubmit}
      rightActions={rightActions}
      title={`${
        isEditMode ? `Edit ${formTemplateName || 'form'}` : 'Create form'
      }`}
      wizardStep={wizardStep}
    />
  );
}
